<template>
    <div class="ticket">
        <div class="ticket-content">
            <div
                :class="[
                    'ticket-content-number',
                    { 'ticket-content-number--no-pax': !fullName },
                ]">
                {{ t({ key: 'shared.ticket', format: LabelFormat.Capitalize }) }}
                {{ ticketNumber }}
            </div>

            <div v-if="fullName" class="ticket-content-pax" v-suppress>
                {{ fullName }}
                <span v-if="gender"> ({{ gender }}) </span>
            </div>

            <Element font="font-body-m-bold" class="ticket-content-description">
                {{ ticket.Description }}
            </Element>

            <TruncateText
                v-if="ticket.CategoryDescription"
                ref="categoryDescription"
                class="ticket-content-category"
                :text="ticket.CategoryDescription"
                :rows="2"
                :hideButton="true" />
            <Fields ref="fieldsComponent" :ticket="ticket" @expand="handleFieldsToggle" />
        </div>
    </div>
</template>

<script lang="ts">
    import { defineComponent, PropType, computed, ref, watch, nextTick } from 'vue';
    import { ActivityTicket } from '@/api/Cart/cart.interfaces';
    import { LabelFormat } from '@shared/interfaces/translation';
    import { t } from '@shared/helpers/translation';
    import { isNotNil } from '@shared/utils/value';
    import TruncateText from '@ui/components/TruncateText/TruncateText.vue';
    import Fields from './Fields/Fields.vue';
    import Element from '@ui/components/Element/Element.vue';

    export default defineComponent({
        name: 'Ticket',
        components: { Fields, TruncateText, Element },
        methods: { t },
        props: {
            ticket: {
                type: Object as PropType<ActivityTicket>,
                required: true,
            },
            ticketNumber: { type: Number, required: true },
        },
        setup(props) {
            const categoryDescription = ref<InstanceType<typeof TruncateText> | null>(null);
            const fieldsComponent = ref<InstanceType<typeof Fields> | null>(null);
            const isUpdating = ref(false);

            const fullName = computed(() => {
                const { FirstName, LastName } = props.ticket.Pax;
                return FirstName && LastName ? `${FirstName} ${LastName}` : undefined;
            });

            const gender = computed(() => {
                const { Gender } = props.ticket.Pax;
                return isNotNil(Gender)
                    ? t({
                          key: 'shared.genders.' + Gender.toLowerCase(),
                      })
                    : undefined;
            });

            watch(
                () => categoryDescription.value?.expanded,
                (newExpanded) => {
                    if (isUpdating.value || !fieldsComponent.value) return;

                    const fieldsExpanded = fieldsComponent.value.isExpanded;

                    if (newExpanded !== fieldsExpanded) {
                        isUpdating.value = true;
                        fieldsComponent.value.toggleExpand();
                        nextTick(() => {
                            isUpdating.value = false;
                        });
                    }
                }
            );

            const handleFieldsToggle = () => {
                if (isUpdating.value || !categoryDescription.value) return;

                const fieldsExpanded = fieldsComponent.value?.isExpanded ?? false;
                const truncateExpanded = categoryDescription.value.expanded;

                if (fieldsExpanded !== truncateExpanded) {
                    isUpdating.value = true;
                    categoryDescription.value.toggleExpanded();
                    nextTick(() => {
                        isUpdating.value = false;
                    });
                }
            };

            return {
                gender,
                fullName,
                LabelFormat,
                categoryDescription,
                fieldsComponent,
                handleFieldsToggle,
            };
        },
    });
</script>

<style scoped lang="scss">
    @forward 'Ticket';
</style>
